import React, { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getVgsapBulletinModels, downloadVgsapBulletinPDF } from "../../service/SalesNewsVgsap";
import { PageLoader } from "../../component/loader/PLoader";
import ErrorHandler from "../../component/errors/ErrorHandler";
import { Box, Typography, Paper, Breadcrumbs, Button, Snackbar, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DownloadIcon from "@mui/icons-material/Download";
import { createExcel } from "../../utils/Utils";
import "./salesNewsVgsap.css";
import { formatDate } from "./vgsapUtils";

function BulletinModels() {
	const { bulletinNo } = useParams();
	const { t } = useTranslation();
	const [selectedIds, setSelectedIds] = useState([]);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

	const { data: modelsData, isLoading, error } = useQuery({
		queryKey: ["vgsapBulletinModels", bulletinNo],
		queryFn: () => getVgsapBulletinModels(bulletinNo),
	});

	// Columns matching RefLib exactly:
	// Model | PRODCODE | Rebate Value | Product QTY Rules | IPSA Product QTY | Valid From | Valid To | Date added | Added By
	const columns = [
		{ field: "model_no", headerName: t("Model"), flex: 1, minWidth: 180 },
		{ field: "prodcode", headerName: t("PRODCODE"), width: 130 },
		{ field: "rebate_value", headerName: t("Rebate Value"), width: 130 },
		{ field: "product_qty_rules", headerName: t("Product QTY Rules"), width: 160 },
		{ field: "ipsa_product_qty", headerName: t("IPSA Product QTY"), width: 150 },
		{ field: "valid_from", headerName: t("Valid From"), width: 120, valueFormatter: (value) => formatDate(value) },
		{ field: "valid_to", headerName: t("Valid To"), width: 120, valueFormatter: (value) => formatDate(value) },
		{ field: "create_dt", headerName: t("Date Added"), width: 120, valueFormatter: (value) => formatDate(value) },
		{ field: "create_opid", headerName: t("Added By"), width: 120 },
	];

	const handleExportExcel = useCallback((exportAll) => {
		const dataRows = (modelsData?.rows || []).map((model, index) => ({
			...model,
			id: model.model_no ? `${model.model_no}_${index}` : String(index),
		}));
		const dataToExport = exportAll ? dataRows : dataRows.filter(row => selectedIds.includes(row.id));

		if (!exportAll && dataToExport.length === 0) {
			setSnackbar({ open: true, message: t('Please select at least one model'), severity: 'warning' });
			return;
		}

		setSnackbar({ open: true, message: t('Processing export...'), severity: 'info' });
		createExcel(columns, dataToExport);
		setTimeout(() => setSnackbar({ open: true, message: t('Export complete'), severity: 'success' }), 500);
	}, [modelsData, selectedIds, columns, t]);

	const handleDownloadPDF = async (bulletinNumbers) => {
		setSnackbar({ open: true, message: t('Processing PDF download...'), severity: 'info' });
		try {
			const result = await downloadVgsapBulletinPDF(bulletinNumbers);
			if (result.url) {
				window.open(result.url, '_blank');
				// Format missing list for display if partial download
				let displayMessage = t('Download ready');
				if (result?.missing && result.missing.length > 0) {
					const missingCount = result.missing.length;
					const previewIds = result.missing.slice(0, 5).join(', ');
					const suffix = missingCount > 5 ? ` ... and ${missingCount - 5} more` : '';
					displayMessage = t('Partial download') + `: ${missingCount} ${t('bulletins without PDFs')} (${previewIds}${suffix})`;
				}
				setSnackbar({ open: true, message: displayMessage, severity: result?.missing?.length > 0 ? 'warning' : 'success' });
			} else if (result.warning) {
				setSnackbar({ open: true, message: result.warning, severity: 'warning' });
			} else {
				setSnackbar({ open: true, message: t('No PDF files found for the selected bulletin(s). PDFs may not have been uploaded yet.'), severity: 'warning' });
			}
		} catch (err) {
			setSnackbar({ open: true, message: t('Failed to download PDF'), severity: 'error' });
		}
	};

	if (isLoading) return <PageLoader show={true} />;
	if (error) return <ErrorHandler title={t("Something went wrong")} desc={t("Unable to load models")} />;

	const rows = (modelsData?.rows || []).map((model, index) => ({
		...model,
		id: model.model_no ? `${model.model_no}_${index}` : String(index),
	}));

	return (
		<Box sx={{ p: 3 }} className="vgsap__page">
			{/* Breadcrumb */}
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
				<Link to="/sales-news/sales-news-vgsap" style={{ textDecoration: "none", color: "#1976d2" }}>
					{t("Sales News (Vistex GSAP)")}
				</Link>
				<Link to={`/sales-news/sales-news-vgsap/detail/${bulletinNo}`} style={{ textDecoration: "none", color: "#1976d2" }}>
					{bulletinNo}
				</Link>
				<Typography color="text.primary">{t("Models")}</Typography>
			</Breadcrumbs>

			{/* Models Content */}
			<Paper sx={{ p: 3 }}>
				<Typography variant="h6" gutterBottom>
					{t("The following models are currently associated with Bulletin")} {bulletinNo}
				</Typography>

				{/* Export toolbar */}
				<Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: 'wrap' }}>
					<Button
						variant="outlined"
						size="small"
						startIcon={<DownloadIcon />}
						disabled={selectedIds.length === 0}
						onClick={() => handleExportExcel(false)}
					>
						{t("Export Selected to Excel")}
					</Button>
					<Button
						variant="outlined"
						size="small"
						startIcon={<DownloadIcon />}
						disabled={rows.length === 0}
						onClick={() => handleExportExcel(true)}
					>
						{t("Export All to Excel")}
					</Button>
					<Button
						variant="outlined"
						size="small"
						startIcon={<DownloadIcon />}
						onClick={() => handleDownloadPDF([bulletinNo])}
					>
						{t("Download Bulletin PDF")}
					</Button>
					<Button
						variant="outlined"
						size="small"
						startIcon={<DownloadIcon />}
						disabled={selectedIds.length === 0}
						onClick={() => {
							// Extract unique bulletin numbers from selected models
							// (on this page all models belong to the same bulletin)
							handleDownloadPDF([bulletinNo]);
						}}
					>
						{t("Download Selected PDF")}
					</Button>
				</Box>

				{/* DataGrid with correct columns */}
				{rows.length === 0 ? (
					<Typography color="text.secondary">{t("No models found for this bulletin")}</Typography>
				) : (
					<div className="vgsap__datagrid-container" style={{ height: 500 }}>
					<DataGrid
						rows={rows}
						columns={columns}
						checkboxSelection
						onRowSelectionModelChange={(ids) => setSelectedIds(ids)}
						rowSelectionModel={selectedIds}
						disableRowSelectionOnClick
						pageSizeOptions={[10, 25, 50]}
						initialState={{
							pagination: { paginationModel: { pageSize: 10 } },
						}}
					/>
					</div>
				)}
			</Paper>

			{/* Snackbar for warnings */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}

export default BulletinModels;
